import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Users, Plus, Trash2, Loader2, UserPlus, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { API } from '../../config/apiBase';


export default function FuncionariosPage() {
    const { user } = useAuth();
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ name: '', email: '', password: '', role: 'funcionario' });

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => { fetchEmployees(); }, []);

    const fetchEmployees = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API}/client/users`, { headers });
            if (res.ok) setEmployees(await res.json());
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.password) {
            toast.error('Preencha todos os campos');
            return;
        }
        setSaving(true);
        try {
            const res = await fetch(`${API}/client/users`, {
                method: 'POST', headers, body: JSON.stringify(form)
            });
            if (res.ok) {
                toast.success('Funcionario cadastrado!');
                setShowForm(false);
                setForm({ name: '', email: '', password: '', role: 'funcionario' });
                fetchEmployees();
            } else {
                const data = await res.json();
                toast.error(data.detail || 'Erro ao cadastrar');
            }
        } catch (e) { toast.error('Erro de conexão'); }
        finally { setSaving(false); }
    };

    const handleDelete = async (userId, name) => {
        if (!window.confirm(`Remover ${name}?`)) return;
        try {
            const res = await fetch(`${API}/client/users/${userId}`, { method: 'DELETE', headers });
            if (res.ok) {
                toast.success('Removido com sucesso');
                setEmployees(employees.filter(e => e.user_id !== userId));
            }
        } catch (e) { toast.error('Erro ao remover'); }
    };

    return (
        <div className="p-4 sm:p-6 max-w-4xl mx-auto" data-testid="funcionarios-page">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <Users className="w-6 h-6" style={{ color: 'var(--sv-gold-primary)' }} />
                    <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>
                        Minha Equipe
                    </h2>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--surface-2)', color: 'var(--muted-2)' }}>
                        {employees.length}
                    </span>
                </div>
                <Button
                    onClick={() => setShowForm(true)}
                    className="bg-[var(--sv-action)] hover:bg-[var(--sv-action-hover)] text-[var(--sv-on-action)] text-xs"
                    data-testid="add-employee-btn"
                >
                    <Plus className="w-4 h-4 mr-1" /> Novo
                </Button>
            </div>

            {/* Form Modal */}
            {showForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={() => setShowForm(false)}>
                    <div className="w-full max-w-md mx-4 rounded-xl p-6" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold flex items-center gap-2" style={{ color: 'var(--text)' }}>
                                <UserPlus className="w-5 h-5" style={{ color: 'var(--sv-gold-primary)' }} />
                                Cadastrar Funcionario
                            </h3>
                            <button onClick={() => setShowForm(false)} style={{ color: 'var(--muted-2)' }}><X className="w-5 h-5" /></button>
                        </div>
                        <form onSubmit={handleCreate} className="space-y-3">
                            <input
                                type="text" placeholder="Nome completo" value={form.name}
                                onChange={e => setForm({...form, name: e.target.value})}
                                className="w-full rounded-lg px-4 py-3 text-sm"
                                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}
                                data-testid="employee-name-input"
                            />
                            <input
                                type="email" placeholder="Email" value={form.email}
                                onChange={e => setForm({...form, email: e.target.value})}
                                className="w-full rounded-lg px-4 py-3 text-sm"
                                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}
                                data-testid="employee-email-input"
                            />
                            <input
                                type="password" placeholder="Senha" value={form.password}
                                onChange={e => setForm({...form, password: e.target.value})}
                                className="w-full rounded-lg px-4 py-3 text-sm"
                                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}
                                data-testid="employee-password-input"
                            />
                            <select
                                value={form.role}
                                onChange={e => setForm({...form, role: e.target.value})}
                                className="w-full rounded-lg px-4 py-3 text-sm"
                                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}
                                data-testid="employee-role-select"
                            >
                                <option value="funcionario">Funcionario</option>
                                <option value="gestor">Gestor</option>
                            </select>
                            <Button type="submit" className="w-full bg-[var(--sv-action)] hover:bg-[var(--sv-action-hover)] text-[var(--sv-on-action)]" disabled={saving} data-testid="save-employee-btn">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                Cadastrar
                            </Button>
                        </form>
                    </div>
                </div>
            )}

            {/* List */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--sv-gold-primary)' }} />
                </div>
            ) : employees.length === 0 ? (
                <div className="text-center py-16">
                    <Users className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--muted-2)' }} />
                    <p className="text-sm" style={{ color: 'var(--muted-2)' }}>Nenhum funcionario cadastrado</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--muted-2)' }}>Clique em "Novo" para adicionar</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {employees.map(emp => (
                        <div key={emp.user_id} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`employee-${emp.user_id}`}>
                            <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: 'var(--surface-2)', color: 'var(--sv-gold-primary)' }}>
                                {emp.name?.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{emp.name}</p>
                                <p className="text-xs truncate" style={{ color: 'var(--muted-2)' }}>{emp.email}</p>
                            </div>
                            <span className="text-xs px-2 py-1 rounded-lg" style={{ 
                                background: emp.role === 'gestor' ? 'rgba(var(--sv-accent-rgb), 0.15)' : 'var(--surface-2)', 
                                color: emp.role === 'gestor' ? 'var(--sv-gold-primary)' : 'var(--muted)' 
                            }}>
                                {emp.role === 'gestor' ? 'Gestor' : 'Funcionario'}
                            </span>
                            <button onClick={() => handleDelete(emp.user_id, emp.name)} className="p-2 rounded-lg transition-colors hover:bg-red-500/10" style={{ color: 'var(--muted-2)' }} data-testid={`delete-employee-${emp.user_id}`}>
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
